import { useEffect, useState } from 'react';
import type { StatsOverview } from '../lib/types';
import { api, ApiError } from '../lib/api';
import { Banner } from './ui';
import { Loading } from '../components/Loading';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/**
 * Who is enrolled, how far they have read, and where the curriculum is not landing.
 *
 * Every measure here is a single series, so the charts carry one hue and no legend:
 * colour is doing magnitude, not identity. Values live in text tokens beside the marks
 * rather than in the mark colour, and each chart has a table beside or behind it, so
 * nothing is conveyed by colour alone.
 */

const dt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—';

const since = (iso: string | null) => {
  if (!iso) return 'never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
};

/** A headline number. No sparkline: with one figure and a caption, a mark would be noise. */
function Stat({ label, value, caption }: { label: string; value: string; caption: string }) {
  return (
    <Card className="vu-stat-card">
      <CardHeader>
        <CardTitle className="vu-kicker" style={{ margin: 0 }}>{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--fs-h1)',
            fontWeight: 'var(--fw-bold)',
            lineHeight: 1.1,
            color: 'var(--text-strong)',
          }}
        >
          {value}
        </div>
        <p className="vu-subtle" style={{ margin: '6px 0 0', fontSize: 'var(--fs-xs)', lineHeight: 1.5 }}>
          {caption}
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Sign-ups by week. Columns, because the reader is comparing magnitude across an
 * ordered time axis; one hue, 4px rounded cap, square on the baseline. Only the tallest
 * and the current week are labelled — a number over every column is noise.
 */
function SignupChart({ weeks }: { weeks: StatsOverview['signups'] }) {
  const max = Math.max(1, ...weeks.map((w) => w.count));
  const peak = weeks.findIndex((w) => w.count === max);
  const H = 132;

  return (
    <>
      <div
        style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-3)', height: H, marginTop: 'var(--space-2)' }}
        aria-hidden="true"
      >
        {weeks.map((w, i) => {
          // Selective labels: the busiest week and the current one, never every column.
          const isPeak = i === peak && max > 0;
          const isLast = i === weeks.length - 1;
          return (
            <div key={w.weekStart} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
              {(isPeak || isLast) && w.count > 0 && (
                <div className="vu-subtle" style={{ fontSize: 'var(--fs-2xs)', textAlign: 'center', marginBottom: 4 }}>
                  {w.count}
                </div>
              )}
              <div
                title={`${w.label}: ${w.count} vendor${w.count === 1 ? '' : 's'}`}
                style={{
                  height: `${Math.round((w.count / max) * (H - 26))}px`,
                  minHeight: w.count > 0 ? 3 : 1,
                  maxWidth: 24,
                  margin: '0 auto',
                  width: '100%',
                  background: w.count > 0 ? 'var(--brand-primary)' : 'var(--border-subtle)',
                  borderRadius: '4px 4px 0 0',
                }}
              />
            </div>
          );
        })}
      </div>
      {/* Hairline baseline, one step off the surface — recessive by design. */}
      <div style={{ height: 1, background: 'var(--border-subtle)' }} />
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 6 }} aria-hidden="true">
        {weeks.map((w) => (
          <div key={w.weekStart} className="vu-subtle" style={{ flex: 1, textAlign: 'center', fontSize: 'var(--fs-2xs)' }}>
            {w.label}
          </div>
        ))}
      </div>
      <table className="sr-only">
        <caption>Vendors who joined, by week</caption>
        <tbody>
          {weeks.map((w) => (
            <tr key={w.weekStart}>
              <th scope="row">Week of {w.label}</th>
              <td>{w.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export function OverviewAdmin() {
  const [stats, setStats] = useState<StatsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .stats()
      .then((s) => !cancelled && setStats(s))
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load the numbers.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <Banner error={error} />;
  if (!stats) return <Loading />;

  const { vendors, progress, tools, tracks, signups, topLessons, coldLessons, needsNudge } = stats;
  const maxTrackFinished = Math.max(1, ...tracks.map((t) => t.finished));

  return (
    <div className="vu-wrap">
      <div className="vu-kicker">Console</div>
      <h1 className="vu-h1">Overview</h1>
      <p className="vu-lede" style={{ maxWidth: '62ch' }}>
        Enrolment and reading across every vendor with a Vendor University account.
        Progress is stored per vendor, so these are real reads, not page views.
      </p>

      <div className="vu-grid vu-grid-4" style={{ marginTop: 'var(--space-6)' }}>
        <Stat
          label="Enrolled"
          value={String(vendors.total)}
          caption={`${vendors.joinedLast30} joined in the last 30 days`}
        />
        <Stat
          label="Active this week"
          value={String(vendors.activeLast7)}
          caption={`${vendors.activeLast30} signed in within 30 days · ${vendors.neverSignedIn} never returned`}
        />
        <Stat
          label="Average completion"
          value={`${progress.averageCompletion}%`}
          caption={`${progress.lessonsReadTotal} lessons read across ${progress.totalLessons} in the library`}
        />
        <Stat
          label="Finished the library"
          value={String(progress.finishedEverything)}
          caption={`${progress.notStarted} have not opened a lesson yet`}
        />
      </div>

      <div className="vu-grid vu-grid-2" style={{ marginTop: 'var(--space-5)', alignItems: 'start' }}>
        <Card>
          <CardHeader>
            <CardTitle className="vu-h3" style={{ fontSize: 'var(--fs-lg)' }}>New vendors, last 8 weeks</CardTitle>
          </CardHeader>
          <CardContent>
            <SignupChart weeks={signups} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="vu-h3" style={{ fontSize: 'var(--fs-lg)' }}>Tools</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Vendors using the checklists</TableCell>
                  <TableCell className="text-right">{tools.checklistUsers}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Checklist items ticked</TableCell>
                  <TableCell className="text-right">{tools.checklistTicks}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Vendors using the listing checker</TableCell>
                  <TableCell className="text-right">{tools.graderUsers}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <p className="vu-subtle" style={{ margin: 'var(--space-4) 0 0', fontSize: 'var(--fs-xs)', lineHeight: 1.55 }}>
              Tool use is the signal that a vendor is applying the course rather than
              skimming it — a vendor who ticks the dispatch checklist is packing to it.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card style={{ marginTop: 'var(--space-5)' }}>
        <CardHeader>
          <CardTitle className="vu-h3" style={{ fontSize: 'var(--fs-lg)' }}>Track completion</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Track</TableHead>
                <TableHead className="text-right">Lessons</TableHead>
                <TableHead className="text-right">Reads</TableHead>
                <TableHead style={{ width: '42%' }}>Vendors who finished it</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tracks.map((t) => (
                <TableRow key={t.slug}>
                  <TableCell style={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)' }}>{t.name}</TableCell>
                  <TableCell className="text-right">{t.lessons}</TableCell>
                  <TableCell className="text-right">{t.reads}</TableCell>
                  <TableCell>
                    {/* Bar-in-table: the value is read from the number, the bar only ranks. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <div style={{ flex: 1, height: 8, background: 'var(--surface-sunken)', borderRadius: 999 }}>
                        <div
                          style={{
                            width: `${Math.round((t.finished / maxTrackFinished) * 100)}%`,
                            height: '100%',
                            background: 'var(--brand-primary)',
                            borderRadius: 999,
                          }}
                        />
                      </div>
                      <span style={{ minWidth: 88, textAlign: 'right', color: 'var(--text-body)' }}>
                        {t.finished} of {vendors.total}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="vu-grid vu-grid-2" style={{ marginTop: 'var(--space-5)', alignItems: 'start' }}>
        <Card>
          <CardHeader>
            <CardTitle className="vu-h3" style={{ fontSize: 'var(--fs-lg)' }}>Most read</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableBody>
                {topLessons.map((l) => (
                  <TableRow key={`${l.track}/${l.title}`}>
                    <TableCell>
                      <div style={{ color: 'var(--text-strong)' }}>{l.title}</div>
                      <div className="vu-subtle" style={{ fontSize: 'var(--fs-xs)' }}>{l.track}</div>
                    </TableCell>
                    <TableCell className="text-right">{l.reads}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="vu-h3" style={{ fontSize: 'var(--fs-lg)' }}>Nobody has read these</CardTitle>
          </CardHeader>
          <CardContent>
            {coldLessons.length === 0 ? (
              <p className="vu-muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
                Every published lesson has been read at least once.
              </p>
            ) : (
              <Table>
                <TableBody>
                  {coldLessons.map((l) => (
                    <TableRow key={`${l.track}/${l.title}`}>
                      <TableCell>
                        <div style={{ color: 'var(--text-strong)' }}>{l.title}</div>
                        <div className="vu-subtle" style={{ fontSize: 'var(--fs-xs)' }}>{l.track}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline">0 reads</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <p className="vu-subtle" style={{ margin: 'var(--space-4) 0 0', fontSize: 'var(--fs-xs)', lineHeight: 1.55 }}>
              A lesson nobody reaches is usually buried in a track, not badly written —
              check where it sits in the guided path.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card style={{ marginTop: 'var(--space-5)' }}>
        <CardHeader>
          <CardTitle className="vu-h3" style={{ fontSize: 'var(--fs-lg)' }}>Needs a nudge</CardTitle>
        </CardHeader>
        <CardContent>
          {needsNudge.length === 0 ? (
            <p className="vu-muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
              Nobody is stalled — every vendor has opened a lesson and signed in this month.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="text-right">Lessons read</TableHead>
                  <TableHead>Why</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {needsNudge.map((v) => (
                  <TableRow key={v.email}>
                    <TableCell>
                      <div style={{ color: 'var(--text-strong)', fontWeight: 'var(--fw-bold)' }}>{v.name}</div>
                      <div className="vu-subtle" style={{ fontSize: 'var(--fs-xs)' }}>{v.email}</div>
                    </TableCell>
                    <TableCell>{dt(v.joinedAt)}</TableCell>
                    <TableCell>{since(v.lastLoginAt)}</TableCell>
                    <TableCell className="text-right">{v.lessonsRead}</TableCell>
                    <TableCell>
                      {/* Reason is written out, never signalled by colour alone. */}
                      <Badge variant={v.lessonsRead === 0 ? 'destructive' : 'secondary'}>{v.reason}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
